import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { IFilterTags } from '@shared/models/tag';
import { ParentTagPipe } from '@shared/pipes/parent-tag.pipe';
import * as fromGlobalStore from '@shared/store';
import { Angulartics2Segment } from 'angulartics2';
import { Subject } from 'rxjs';

@Component({
  selector: 'ten-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatLegacyButtonModule,
    MatLegacyCheckboxModule,
    ParentTagPipe,
    TranslateModule,
  ],
})
export class FilterModalComponent implements OnInit, OnDestroy {
  public numberOfAppliedFilterTags = 0;
  public tags: IFilterTags[];
  private unsubscribe$ = new Subject<void>();
  public tagsInLeftColumn: IFilterTags[] = [];
  public tagsInRightColumn: IFilterTags[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<FilterModalComponent>,
    private ngrxStore: Store<any>,
    private angulartics2Segment: Angulartics2Segment
  ) {}

  public ngOnInit() {
    this.tags = this.data.displayData;
    this.tags.forEach((tag: IFilterTags) => {
      if (tag.selected) {
        this.numberOfAppliedFilterTags++;
      }
      tag.subcategories?.forEach((sc) => {
        if (sc.selected) {
          this.numberOfAppliedFilterTags++;
        }
      });
    });
    this.tags.sort(this.sortBySubcategoriesLength);
    for (let i = 0; i < this.tags.length; i++) {
      if (i % 2 === 0) {
        this.tagsInLeftColumn.push(this.tags[i]);
      } else {
        this.tagsInRightColumn.push(this.tags[i]);
      }
    }
  }

  public onParentCategoryClick(category: IFilterTags) {
    const trackCategories = [];
    trackCategories.push(category.id);

    if (category.selected) {
      this.numberOfAppliedFilterTags++;
      if (category.subcategories?.length) {
        category.subcategories.forEach((subcategory: IFilterTags) => {
          if (!subcategory.selected) {
            subcategory.selected = true;
            this.numberOfAppliedFilterTags++;
            trackCategories.push(subcategory.id);
          }
        });
      }

      this.angulartics2Segment.eventTrack('MemberBenefits Category Filter(s) selected', {
        categories: trackCategories,
      });
    } else {
      this.numberOfAppliedFilterTags--;
      if (category.subcategories?.length) {
        category.subcategories.forEach((subcategory: IFilterTags) => {
          if (subcategory.selected) {
            subcategory.selected = false;
            this.numberOfAppliedFilterTags--;
            trackCategories.push(subcategory.id);
          }
        });
      }

      this.angulartics2Segment.eventTrack('MemberBenefits Category Filter(s) cleared', { categories: trackCategories });
    }
  }

  public onSubcategoryClick(category: IFilterTags, parentCategory: IFilterTags) {
    const trackCategories = [];
    trackCategories.push(category.id);
    if (category.selected) {
      this.numberOfAppliedFilterTags++;
      if (!parentCategory.subcategories.some((sc) => !sc.selected)) {
        parentCategory.selected = true;
        this.numberOfAppliedFilterTags++;
        trackCategories.push(parentCategory.id);
      }
      this.angulartics2Segment.eventTrack('MemberBenefits Category Filter(s) set', { categories: trackCategories });
    } else {
      this.numberOfAppliedFilterTags--;
      if (parentCategory.selected) {
        parentCategory.selected = false;
        this.numberOfAppliedFilterTags--;
        trackCategories.push(parentCategory.id);
      }
      this.angulartics2Segment.eventTrack('MemberBenefits Category Filter(s) cleared', { categories: trackCategories });
    }
  }

  public updateFilter(tags: number[]) {
    this.ngrxStore.dispatch(
      new fromGlobalStore.UpdateCurrentFilters({
        module: this.data.module,
        filterName: this.data.filterName,
        filterValue: tags,
        disableScroll: true,
        filterType: this.data.type,
      })
    );
  }

  public onApplyFilters() {
    const flatList: number[] = [];
    this.tags.forEach((tag: IFilterTags) => {
      if (tag.selected) {
        flatList.push(tag.id);
      }
      tag.subcategories?.forEach((sc) => {
        if (sc.selected) {
          flatList.push(sc.id);
        }
      });
    });
    this.updateFilter(flatList);
    this.closeModal();
  }

  public closeModal() {
    this.dialogRef.close();
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private sortBySubcategoriesLength = (a: IFilterTags, b: IFilterTags) => {
    if (a.subcategories?.length > 0 && b.subcategories?.length > 0) {
      return a.name.localeCompare(b.name);
    } else {
      return (a.subcategories?.length || 0) - (b.subcategories?.length || 0) || a.name.localeCompare(b.name);
    }
  };
}
